import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { StarRating } from '../components/StarRating';
import { validatePassword } from '../lib/passwordRules';

export default function TenantDashboard({ user }) {
  const navigate = useNavigate();

  // --- Section 3 (Reputation) state ---
  const [tenantProfile, setTenantProfile] = useState(null);   // row in `tenants` table that represents this user
  const [reviewsAboutMe, setReviewsAboutMe] = useState([]);   // reviews authored about me
  const [myReviews, setMyReviews] = useState([]);             // reviews I authored
  const [viewedProfiles, setViewedProfiles] = useState([]);   // recently viewed (localStorage)
  const [loading, setLoading] = useState(true);

  // --- Section 1 (Account) state ---
  const [showChangePw, setShowChangePw] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);   // { type: 'success' | 'error', text }
  const [pwSaving, setPwSaving] = useState(false);
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user?.name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) { setNameMsg({ type: 'error', text: 'Name cannot be empty.' }); return; }
    setNameSaving(true); setNameMsg(null);
    const { error } = await supabase.auth.updateUser({ data: { name: trimmed } });
    if (error) { setNameMsg({ type: 'error', text: error.message }); setNameSaving(false); return; }
    if (user?.id) { await supabase.from('profiles').update({ name: trimmed }).eq('id', user.id); }
    setDisplayName(trimmed);
    setEditingName(false);
    setNameSaving(false);
    setNameMsg({ type: 'success', text: 'Name updated.' });
    setTimeout(() => setNameMsg(null), 1800);
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    async function loadData() {
      // Best-effort: match the tenant profile row by name. The schema doesn't
      // currently have an auth_user_id FK on the tenants table.
      const { data: tProf } = await supabase.from('tenants').select('*').ilike('name', user.name).maybeSingle();
      setTenantProfile(tProf || null);

      // Reviews ABOUT me (someone else wrote about my tenant profile)
      let aboutMe = [];
      if (tProf?.id) {
        const { data } = await supabase.from('reviews').select('*').eq('profile_id', tProf.id).eq('profile_type', 'tenant').order('created_at', { ascending: false });
        aboutMe = data || [];
      }
      setReviewsAboutMe(aboutMe);

      // Reviews I wrote (about property management)
      const { data: mine } = await supabase.from('reviews').select('*').eq('author', user.name).order('created_at', { ascending: false });
      const enriched = await Promise.all((mine || []).map(async (r) => {
        const table = r.profile_type === 'manager' ? 'managers' : 'tenants';
        const { data: prof } = await supabase.from(table).select('id, name').eq('id', r.profile_id).maybeSingle();
        return { ...r, profileName: prof?.name || 'Unknown profile' };
      }));
      setMyReviews(enriched);

      // Recently viewed (localStorage)
      try {
        const stored = JSON.parse(localStorage.getItem('rentrep_recently_viewed') || '[]');
        setViewedProfiles(stored.slice(0, 6));
      } catch(e) {}

      setLoading(false);
    }
    loadData();
  }, [user, navigate]);

  const cardStyle = {
    background: '#FAF6EC',
    borderRadius: 16,
    borderTop: '1px solid rgba(184,137,58,0.22)',
    borderRight: '1px solid rgba(184,137,58,0.22)',
    borderBottom: '1px solid rgba(184,137,58,0.22)',
    borderLeft: '1px solid rgba(184,137,58,0.22)',
    boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
  };

  const isTenant = user?.role === 'tenant';
  const plan = isTenant ? 'Silver' : 'No active plan';
  const planPrice = isTenant ? '$9.99/mo · 3 unlocks/month' : null;

  const reputation = reviewsAboutMe.length === 0 ? null
    : reviewsAboutMe.reduce((s, r) => s + (r.rating || 0), 0) / reviewsAboutMe.length;

  // --- Section 1 handlers ---
  const pwChecks = validatePassword(newPw);
  const pwOk = pwChecks.every(c => c.ok);
  const pwMatch = newPw.length > 0 && newPw === confirmPw;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (!pwOk) { setPwMsg({ type: 'error', text: 'Password does not meet all the requirements below.' }); return; }
    if (!pwMatch) { setPwMsg({ type: 'error', text: 'The two passwords do not match.' }); return; }
    setPwSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPw });
    setPwSaving(false);
    if (error) { setPwMsg({ type: 'error', text: error.message }); return; }
    setPwMsg({ type: 'success', text: 'Password updated successfully.' });
    setNewPw(''); setConfirmPw('');
    setTimeout(() => { setShowChangePw(false); setPwMsg(null); }, 1800);
  };

  // --- Section 3 handlers ---
  const handleDeleteMyReview = async (reviewId) => {
    if (!window.confirm('Delete this review? This cannot be undone.')) return;
    const { error } = await supabase.from('reviews').delete().eq('id', reviewId);
    if (error) { alert('Could not delete: ' + error.message); return; }
    setMyReviews(prev => prev.filter(r => r.id !== reviewId));
  };

  if (!user) return null;

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      <div className="container" style={{ paddingTop: 20, paddingBottom: 32 }}>

        {/* Greeting strip */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Your account</div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, fontWeight: 400, color: '#0F2C4E', lineHeight: 1.2 }}>Welcome back, {(displayName || user.name)?.split(' ')[0]}.</div>
        </div>

        {/* ─── Account ─── */}
        <SectionHeader title="Account" subtitle="Your contact info, login, and password" />
        <div style={{ ...cardStyle, padding: 18, marginBottom: 24, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
          {/* Editable name */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(184,137,58,0.12)', fontSize: 13, gap: 10, flexWrap: 'wrap' }}>
            <span style={{ color: '#6B6358' }}>Name</span>
            {editingName ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', flex: 1 }}>
                <input
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  autoFocus
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid rgba(184,137,58,0.45)', background: '#fff', fontSize: 13, minWidth: 160 }}
                />
                <button onClick={handleSaveName} disabled={nameSaving} style={{ background: '#0F2C4E', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {nameSaving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => { setEditingName(false); setNameInput(displayName); setNameMsg(null); }} style={{ background: 'transparent', color: '#6B6358', border: '1.5px solid rgba(184,137,58,0.45)', padding: '6px 12px', borderRadius: 50, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: '#0F2C4E', fontWeight: 500 }}>{displayName || '—'}</span>
                <button onClick={() => { setEditingName(true); setNameInput(displayName); }} style={{ background: 'none', border: 'none', color: '#1A4C8B', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                  Edit
                </button>
              </div>
            )}
          </div>
          {nameMsg && (
            <div style={{ fontSize: 12, padding: '4px 0', color: nameMsg.type === 'success' ? '#1A7F37' : '#C0392B' }}>
              {nameMsg.type === 'success' ? '✓ ' : '⚠️ '}{nameMsg.text}
            </div>
          )}
          <Row label="Email" value={user.email} />
          <Row label="Role" value={isTenant ? 'Renter / Tenant' : 'Property Management'} />
          {tenantProfile?.initials && <Row label="Profile initials" value={tenantProfile.initials} />}
          <div style={{ borderTop: '1px solid rgba(184,137,58,0.22)', marginTop: 12, paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>Password</div>
                <div style={{ fontSize: 12, color: '#6B6358', marginTop: 2 }}>Last changed: managed by Supabase Auth</div>
              </div>
              <button onClick={() => { setShowChangePw(s => !s); setPwMsg(null); }} style={btnOutline}>
                {showChangePw ? 'Cancel' : 'Change password'}
              </button>
            </div>
            {showChangePw && (
              <form onSubmit={handleChangePassword} style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label>New password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Create a strong password" required minLength={8} style={{ paddingRight: 40, width: '100%', boxSizing: 'border-box' }} />
                    <button type="button" onClick={() => setShowNewPw(s => !s)} style={eyeBtn} aria-label={showNewPw ? 'Hide password' : 'Show password'}>
                      {showNewPw ? '🙈' : '👁'}
                    </button>
                  </div>
                  {newPw.length > 0 && (
                    <div style={{ marginTop: 8, padding: '10px 12px', background: 'rgba(184,137,58,0.10)', borderRadius: 8, border: '1px solid rgba(184,137,58,0.22)' }}>
                      {pwChecks.map(c => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: c.ok ? '#1A7F37' : '#6B6358', lineHeight: 1.6 }}>
                          <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center', fontWeight: 700 }}>{c.ok ? '✓' : '○'}</span>
                          {c.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Confirm new password</label>
                  <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Re-enter the same password" required style={{
                    width: '100%', boxSizing: 'border-box',
                    borderColor: confirmPw && !pwMatch ? '#C0392B' : (pwMatch ? '#1A7F37' : undefined),
                  }} />
                  {confirmPw && !pwMatch && <div style={{ marginTop: 6, fontSize: 12, color: '#C0392B' }}>⚠️ The passwords don't match yet.</div>}
                  {pwMatch && <div style={{ marginTop: 6, fontSize: 12, color: '#1A7F37' }}>✓ Passwords match.</div>}
                </div>
                {pwMsg && (
                  <div style={{ fontSize: 13, marginBottom: 10, color: pwMsg.type === 'success' ? '#1A7F37' : '#C0392B' }}>
                    {pwMsg.type === 'success' ? '✓ ' : '⚠️ '}{pwMsg.text}
                  </div>
                )}
                <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', opacity: pwSaving ? 0.7 : 1 }} disabled={pwSaving}>
                  {pwSaving ? 'Updating...' : 'Update password'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* ─── Plan & Payment ─── */}
        <SectionHeader title="Plan & Payment" subtitle="What you're on, how to upgrade, payment method" />
        <div style={{ ...cardStyle, padding: 18, marginBottom: 24, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Current plan</div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, fontWeight: 400, color: '#0F2C4E' }}>{plan}</div>
              {planPrice && <div style={{ fontSize: 13, color: '#6B6358', marginTop: 2 }}>{planPrice}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              {isTenant && <span style={{ background: '#C9972A', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, letterSpacing: '0.04em' }}>ACTIVE</span>}
              <button onClick={() => navigate('/pricing')} className="btn btn-gold btn-sm">{isTenant ? 'Manage plan' : 'Upgrade'}</button>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(184,137,58,0.22)', marginTop: 14, paddingTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E', marginBottom: 4 }}>Payment method</div>
            <div style={{ fontSize: 12, color: '#6B6358', lineHeight: 1.6, marginBottom: 10 }}>
              Free during launch — no payment method required yet. Once paid tiers turn on, you'll add a card here.
            </div>
            <button disabled style={{ ...btnOutline, opacity: 0.55, cursor: 'not-allowed' }}>Add payment method (coming soon)</button>
          </div>
        </div>

        {/* ─── Reputation ─── */}
        <SectionHeader title="Reputation" subtitle="How you're scored, who reviewed you, what you've reviewed" />
        <div style={{ ...cardStyle, padding: 18, marginBottom: 24, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
          {/* Your score */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Your overall score</div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: reputation >= 4 ? '#1B7A4B' : reputation >= 3 ? '#C9972A' : reputation ? '#C0392B' : '#6B6358', lineHeight: 1 }}>
                {reputation ? reputation.toFixed(1) : '—'}
              </div>
              <div style={{ fontSize: 12, color: '#6B6358', marginTop: 4 }}>
                {reviewsAboutMe.length === 0 ? 'No reviews yet' : `${reviewsAboutMe.length} review${reviewsAboutMe.length === 1 ? '' : 's'} from property management`}
              </div>
            </div>
            {reputation && <StarRating rating={reputation} size={20} />}
          </div>

          {/* Reviews about me */}
          <SubHeader>Reviews about you</SubHeader>
          {loading ? <Loading /> :
            reviewsAboutMe.length === 0
              ? <Empty>No property management has reviewed you yet. Your reputation builds as you rent.</Empty>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
                  {reviewsAboutMe.map(r => (
                    <div key={r.id} style={miniCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>{r.author}</div>
                          <div style={{ fontSize: 11, color: '#6B6358', marginTop: 2 }}>{r.date}</div>
                        </div>
                        <StarRating rating={r.rating} size={12} />
                      </div>
                      {r.text && <p style={{ fontSize: 12.5, color: '#2C2C2C', lineHeight: 1.6, margin: 0 }}>{r.text}</p>}
                    </div>
                  ))}
                </div>
          }

          {/* Profiles you've viewed */}
          <SubHeader>Profiles you've viewed</SubHeader>
          {viewedProfiles.length === 0
            ? <Empty>You haven't looked up any property management yet.</Empty>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                {viewedProfiles.map(p => (
                  <button key={p.id} onClick={() => navigate('/profile/' + p.id)} style={{
                    ...miniCard, cursor: 'pointer', textAlign: 'left',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#0F2C4E' }}>{p.name}</span>
                    <span style={{ fontSize: 11, color: '#6B6358' }}>View again →</span>
                  </button>
                ))}
              </div>
          }

          {/* My reviews (edit/delete) */}
          <SubHeader>Reviews you've written</SubHeader>
          {loading ? <Loading /> :
            myReviews.length === 0
              ? <Empty>You haven't written any reviews yet. <a href="#" onClick={(e) => { e.preventDefault(); navigate('/find?type=manager'); }} style={{ color: '#0F2C4E', fontWeight: 600 }}>Write your first review →</a></Empty>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {myReviews.map(r => (
                    <div key={r.id} style={miniCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>{r.profileName}</div>
                          <div style={{ fontSize: 11, color: '#6B6358', marginTop: 2 }}>{r.date} · {r.profile_type === 'manager' ? 'Property Management' : 'Tenant'}</div>
                        </div>
                        <StarRating rating={r.rating} size={12} />
                      </div>
                      {r.text && <p style={{ fontSize: 12.5, color: '#2C2C2C', lineHeight: 1.6, marginBottom: 10 }}>{r.text}</p>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => navigate(`/review/${r.profile_id}?edit=${r.id}`)} style={smallBtnNavy}>Edit</button>
                        <button onClick={() => handleDeleteMyReview(r.id)} style={smallBtnRed}>Delete</button>
                        <button onClick={() => navigate(`/profile/${r.profile_id}`)} style={smallBtnSubtle}>View profile</button>
                      </div>
                    </div>
                  ))}
                </div>
          }
        </div>

      </div>
    </div>
  );
}

// ─── small presentational helpers ───
function SectionHeader({ title, subtitle }) {
  return (
    <div>
      <div style={{
        background: '#E5D5B7',
        color: '#0F2C4E',
        fontFamily: "'DM Serif Display', serif",
        fontSize: 13,
        fontWeight: 400,
        textAlign: 'center',
        letterSpacing: '0.10em',
        padding: '5px 22px',
        borderRadius: '16px 16px 0 0',
        textShadow: '0 1px 1px rgba(255,255,255,0.35)',
        boxShadow: '0 2px 6px rgba(120,90,30,0.14)',
      }}>{title.toUpperCase()}</div>
      {subtitle && (
        <div style={{
          fontSize: 12, color: '#6B6358',
          background: '#FAF6EC',
          padding: '8px 18px 0',
          borderLeft: '1px solid rgba(184,137,58,0.22)',
          borderRight: '1px solid rgba(184,137,58,0.22)',
        }}>{subtitle}</div>
      )}
    </div>
  );
}
function SubHeader({ children }) {
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(184,137,58,0.22)' }}>
      <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(184,137,58,0.12)', fontSize: 13 }}>
      <span style={{ color: '#6B6358' }}>{label}</span>
      <span style={{ color: '#0F2C4E', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
}
function Loading() { return <div style={{ fontSize: 12, color: '#6B6358', fontStyle: 'italic', padding: '8px 0', marginBottom: 14 }}>Loading…</div>; }
function Empty({ children }) { return <div style={{ fontSize: 12.5, color: '#6B6358', fontStyle: 'italic', padding: '8px 0', marginBottom: 18 }}>{children}</div>; }

const miniCard = { background: '#F5EFE3', border: '1px solid rgba(184,137,58,0.18)', borderRadius: 12, padding: 12 };
const btnOutline = { background: 'transparent', border: '1.5px solid #0F2C4E', color: '#0F2C4E', padding: '7px 14px', borderRadius: 50, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const smallBtnNavy = { flex: 1, padding: '7px 10px', borderRadius: 50, border: '1.5px solid #0F2C4E', background: 'transparent', color: '#0F2C4E', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const smallBtnRed = { flex: 1, padding: '7px 10px', borderRadius: 50, border: '1.5px solid #C0392B', background: 'transparent', color: '#C0392B', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const smallBtnSubtle = { flex: 1, padding: '7px 10px', borderRadius: 50, border: '1.5px solid rgba(184,137,58,0.45)', background: '#FAF6EC', color: '#6B6358', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const eyeBtn = { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B6358', fontSize: 14 };
